if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        this.mobilePhotoDown = (event) => {
          this.mobilePhotoPointer = { x: event.clientX, y: event.clientY, moved: false };
        };
        this.mobilePhotoMove = (event) => {
          const start = this.mobilePhotoPointer;
          if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 12) start.moved = true;
        };
        this.mobilePhotoCancel = () => { this.mobilePhotoPointer = { moved: true }; };
        this.mobilePhotoClick = this.onMobilePhotoTap.bind(this);
        this.addEventListener('pointerdown', this.mobilePhotoDown);
        this.addEventListener('pointermove', this.mobilePhotoMove);
        this.addEventListener('pointercancel', this.mobilePhotoCancel);
        this.addEventListener('click', this.mobilePhotoClick, true);
      }

      disconnectedCallback() {
        this.removeEventListener('pointerdown', this.mobilePhotoDown);
        this.removeEventListener('pointermove', this.mobilePhotoMove);
        this.removeEventListener('pointercancel', this.mobilePhotoCancel);
        this.removeEventListener('click', this.mobilePhotoClick, true);
        this.mobilePhotoPointer = null;
      }

      onMobilePhotoTap(event) {
        if (this.mql.matches || event.detail === 0 || this.mobilePhotoPointer?.moved) return;
        const opener = event.target.closest('.product__modal-opener--image');
        const slide = opener?.closest('[data-media-id]');
        if (!slide || !this.elements.viewer.contains(slide)) return;
        if (!slide.querySelector('.product__modal-opener--image')) return;
        const icon = slide.querySelector('.product__media-icon');
        if (icon && icon.getClientRects().length) {
          const rect = icon.getBoundingClientRect();
          if (event.clientX >= rect.left && event.clientX <= rect.right &&
              event.clientY >= rect.top && event.clientY <= rect.bottom) return;
        }
        const photos = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'))
          .filter((item) => item.querySelector('.product__modal-opener--image'));
        if (photos.length < 2) return;
        const index = photos.indexOf(slide);
        if (index < 0) return;
        const next = photos[(index + 1) % photos.length];
        next.querySelectorAll('img[loading="lazy"]').forEach((image) => { image.loading = 'eager'; });
        event.preventDefault();
        event.stopImmediatePropagation();
        this.setActiveMedia(next.dataset.mediaId, false);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
